import React, { useEffect, useState, useContext, useRef } from 'react';
import { useSelector } from 'react-redux';
import { Modal, Icon } from '@PetSureAustralia/petsure-react-ui-library/distLib/components';
import PropTypes from 'prop-types';
import './claimsBenefitCalculationPopup.scss';
import { DateToShortReadableDate, RichTextParser } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { TooltipTrigger } from '../../tooltipTrigger/tooltipTrigger';
import { useGetClaimRemittanceQuery } from '../../../data/claim';
import Loading from '../../shared/Loading/Loading';
import { FeatureFlagContext } from '../../../contextProviders/featureFlagContext';
import { SLICES } from '../../../constants/slices';
import { useGetGeneralTooltipContentQuery } from '../../../data/kontentAi';
import { GENERAL_TOOLTIP_CODENAME } from '../../../constants/cmsCodeName';
import { EXCESS_TYPE } from '../../../constants/constants';

export const ClaimsBenefitCalculationPopup = ({ onClose = () => {}, claimNo, logicalNo, excessType }) => {
  const { cspConfiguration } = useContext(FeatureFlagContext);
  const isExcessEnabled = cspConfiguration?.IsExcessEnabled || false;
  const isKontentAiPreviewEnabled = cspConfiguration?.IsKontentAIPreviewEnabled || false;
  const { authDetails } = useSelector((state) => state?.[SLICES.AUTHENTICATION]);
  const { auth0AccessToken } = authDetails;
  const { data: benefitCalculation, isLoading: benefitCalculationLoading } = useGetClaimRemittanceQuery(
    { claimNumber: claimNo, auth0Token: auth0AccessToken },
    {
      skip: !auth0AccessToken
    }
  );
  const filteredBenefit = benefitCalculation?.find((item) => item.LogicalClaimNumber === logicalNo);
  const [rowTreatmentLines, setRowTreatmentLines] = useState();
  const [showExcessTooltip, setShowExcessTooltip] = useState();
  const remarksSection = rowTreatmentLines?.filter((item) => item.remarkNumber);
  const { data: generalTooltip } = useGetGeneralTooltipContentQuery({
    previewEnabled: isKontentAiPreviewEnabled,
    element: [GENERAL_TOOLTIP_CODENAME.EXCESS_PER_CONDITION_TOOLTIP]
  });
  const excessTooltip =
    generalTooltip?.content?.[GENERAL_TOOLTIP_CODENAME.EXCESS_PER_CONDITION_TOOLTIP]?.[
      GENERAL_TOOLTIP_CODENAME.EXCESS_PER_CONDITION_TOOLTIP
    ]?.value || '';

  useEffect(() => {
    if (filteredBenefit?.TreatmentLines) {
      let remarkCounter = 0;
      const lines = filteredBenefit.TreatmentLines.map((item, index) => {
        const remarkNumber = item.remarkCode ? (remarkCounter += 1) : '';
        return { ...item, remarkNumber, index };
      });
      setRowTreatmentLines(lines);
    }
  }, [benefitCalculationLoading, filteredBenefit]);

  /*
    TODO: This should be moved to UI Library for modals
    */
  const modalRef = useRef(null);
  useEffect(() => {
    modalRef.current.focus();
  }, []);

  return (
    <div
      role="alertdialog"
      aria-modal="true"
      aria-labelledby="dialog_label"
      aria-describedby="dialog_desc"
      ref={modalRef}
      tabIndex={-1}
    >
      <Modal
        customCssClass="benefitCalculationPopup"
        size="small"
        isContentScrollable
        open
        onClose={onClose}
        header={<h2 className="modal-title">Benefit Calculation</h2>}
        footer={
          <button
            type="button"
            className="button primary outline"
            onClick={onClose}
          >
            <span className="labelText">Close</span>
          </button>
        }
        showHeaderDivider
        showFooterDivider
      >
        <section>
          <p className="claim-number">
            <strong>Claim Number:</strong> <strong>{logicalNo?.split('/')[0]}</strong>
          </p>
          <p>Your benefit is calculated as follows:</p>
          {benefitCalculationLoading ? (
            <Loading />
          ) : (
            <>
              <table className="benefit-table">
                <thead>
                  <tr>
                    <th>
                      <strong>Treatment Date</strong>
                    </th>
                    <th>
                      <strong>Treatment Item</strong>
                    </th>
                    <th className="amount">
                      <strong>Claimed Amount</strong>
                    </th>
                    <th className="amount">
                      <strong>Benefit Amount</strong>
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {rowTreatmentLines?.map((item) => (
                    <tr key={`item_${item.index + 1}`}>
                      <td>{DateToShortReadableDate(new Date(item.treatmentDate))}</td>
                      <td>{item.treatmentDescription}</td>
                      <td className="amount">${item.treatmentClaimedAmount.toFixed(2)}</td>
                      <td className="amount">
                        ${item.treatmentBenefitPaid.toFixed(2)}
                        {item.remarkNumber && <sup>{item.remarkNumber}</sup>}
                      </td>
                    </tr>
                  ))}
                  <tr>
                    <td aria-label="divider" />
                  </tr>
                </tbody>
                <tfoot>
                  <tr>
                    <td
                      colSpan="2"
                      aria-label="sub total"
                    />
                    <td>Sub Total</td>
                    <td className="amount">${filteredBenefit?.Subtotal.toFixed(2)}</td>
                  </tr>
                  {isExcessEnabled && (
                    <tr>
                      <td
                        colSpan="2"
                        aria-label="excess"
                      />
                      <td>
                        Excess {` `}
                        {excessType === EXCESS_TYPE.PER_CONDITION && (
                          <TooltipTrigger
                            id="tip-click"
                            mode="click"
                            trigger={<Icon icon="information" />}
                            content={<>{RichTextParser(excessTooltip)}</>}
                          />
                        )}
                      </td>
                      <td className="amount">${filteredBenefit?.Excess.toFixed(2)}</td>
                    </tr>
                  )}
                  <tr>
                    <td
                      colSpan="2"
                      aria-label="total benefit"
                    />
                    <td>Total Benefit</td>
                    <td className="amount">${filteredBenefit?.BenefitPaid.toFixed(2)}</td>
                  </tr>
                </tfoot>
              </table>
              {filteredBenefit?.ClaimNotes.length > 0 && (
                <>
                  <p>
                    <strong>Claim Notes</strong>
                  </p>
                  {filteredBenefit?.ClaimNotes.map((note) => (
                    <p key={note.ClaimNote}>{note.ClaimNote}</p>
                  ))}
                </>
              )}

              {remarksSection?.map((item) => (
                <p key={`remark_${item.remarkNumber}`}>
                  <sup>{item.remarkNumber}</sup> {item.remarkCode}
                </p>
              ))}
            </>
          )}
        </section>
      </Modal>
    </div>
  );
};

ClaimsBenefitCalculationPopup.propTypes = {
  onClose: PropTypes.func,
  claimNo: PropTypes.string,
  logicalNo: PropTypes.string,
  excessType: PropTypes.string
};
